// @react
import React from 'react'
import { ReactElement, useEffect } from 'react'
// @common
import colors from '@bit/atd.web.colors'
import { makeStyles } from '@material-ui/core/styles'
import { ExternalRouteNames, RouteNames } from 'constants/routeNames'
import SortUtils from 'utils/sortutils'
import Helper from 'utils/Helper.class'
import Typography from '@material-ui/core/Typography'
// @components
import Lozenge from '@bit/atd.web.lozenge'
import Box from '@bit/atd.web.box'
import Table from '@bit/atd.web.table'
import ButtonNav from '@bit/atd.web.button-nav'
import Icon from '@bit/atd.web.icons'
import Spacer from '@bit/atd.web.spacer'
import Button from '@bit/atd.web.button'
import ConditionalLoader from '@bit/atd.web.loaders.conditional-loader'
// @module components
import useTable from 'modules/common/hooks/useTable'
import QueryError from 'modules/common/components/Errors/QueryError'
import PaginationWrapper from 'modules/common/components/PaginationWrapper'
import PaginationScrollWrapper from 'modules/common/components/PaginationScrollWrapper'
// @graphql
import { useQuery } from '@apollo/client'
import { GET_COURSES } from 'graphql/queries/learning.query'
import {
  LearningActivityOrderByList,
  LearningCourseQuery,
  LearningCourseQueryVariables,
} from 'lib/graphql/graphqlTypes'
import strings from 'lib/constants/strings'
/**
 * 4.0.1.1 MyATD - ActivityHistory - Learning - Courses
 * UX: https://www.figma.com/file/jCo0D2EYkVbuBX1TjR1enA/ATD-MyATD-Desktop-2020?node-id=3123%3A16072
 *
 * Course	title
 * Course	status
 * Course	date
 * Course	location
 * Course	credits
 * Course	creditType
 * Course	certificateUrl
 *
 */

const useStyles = makeStyles({
  title: {
    display: 'block',
    fontWeight: 600,
  },
  subtitle: {
    color: colors.gray500,
    fontWeight: 600,
  },
  earned: {
    whiteSpace: 'nowrap',
  },
  textTitle: {
    color: '#2E2d31',
    fontSize: '18px',
    fontWeight: 500,
    lineHeight: '26px',
    marginBottom: '8px',
  },
  textDescription: {
    color: '#424140',
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '24px',
  },
  textAction: {
    marginTop: '24px',
    flexGrow: 1,
    display: 'flex',
    '& > a': {
      alignSelf: 'flex-end',
    },
  },
})

const Courses = (): ReactElement => {
  /******************************************
   * HOOKS
   ******************************************/

  const classes = useStyles()

  const {
    Pagination,
    paginationProps,
    ordering,
    inputVal,
    setRefetch,
    sortHandler,
  } = useTable<LearningCourseQueryVariables>({
    orderBy: LearningActivityOrderByList.TITLE,
  })

  const { loading: isLoading, error: isError, data, refetch } = useQuery<
    LearningCourseQuery,
    LearningCourseQueryVariables
  >(GET_COURSES, {
    variables: {
      input: inputVal,
    },
  })

  useEffect(() => setRefetch(refetch), [refetch, setRefetch])

  /******************************************
   * FUNCTIONS
   ******************************************/

  const renderCEU = (content: any) => {
    const { completion, credits } = content
    if (!credits || credits === []) return null
    // @it-ignore
    const percentage = ['Passed', 'Completed'].indexOf(completion) > -1 ? 1 : 0 //@todo: move to enum
    return credits.map((credit: { type: string; value: number }) => (
      <div key={credit.type} className={classes.earned}>
        <strong>{credit.type}: </strong>
        {credit.value * percentage}/{credit.value}{' '}
      </div>
    ))
  }

  if (isError) {
    return <QueryError />
  }
  const contentData: LearningCourseQuery['learningCourse']['data'] | [] =
    data?.learningCourse?.data || []

  const meta = data?.learningCourse?.meta

  const tableRows = () => {
    if (!data?.learningCourse?.data) return [[<div key={1} />]]
    return contentData.map((item) => {
      const content = item
      return [
        <span key={1}>
          <strong className={classes.title}>{content.name}</strong>
          <strong className={classes.subtitle}>Individual Course</strong>
        </span>,
        <Lozenge key={2} variant="outline" aria-label="In-Progress">
          {content.completion}
        </Lozenge>,
        <span key={3}>
          <strong className={classes.title}>
            {Helper.rangeDateFormatter(content.startDate, content.endDate)}
          </strong>
          {content.section ? (
            <strong className={classes.subtitle}>{content.section}</strong>
          ) : null}
        </span>,
        <span key={4}>{renderCEU(content)}</span>,
      ]
    })
  }

  /**
   *
   */
  const rowActions = () => {
    if (!contentData) {
      return [[<div key={1} />]]
    }
    return contentData.map((item) => {
      const actions = []
      if (item.viewUrl) {
        actions.push(
          <ButtonNav
            key={1}
            variant="secondary"
            label={strings.ACTIVITY_HISTORY.TABLE.VIEW}
            linkType="external"
            href={item.viewUrl}
          />,
        )
      }
      if (item.launchUrl) {
        actions.push(
          <ButtonNav
            key={1}
            variant="secondary"
            label={strings.ACTIVITY_HISTORY.TABLE.LAUNCH}
            linkType="external"
            href={item.launchUrl}
          />,
        )
      }
      if (item.certificates && item.certificates.length > 0) {
        item.certificates.forEach((certificate) => {
          actions.push(
            <ButtonNav
              key={certificate.name} // @todo was ID, check it
              variant="secondary"
              label={strings.ACTIVITY_HISTORY.TABLE.DOWNLOAD_CERTIFICATE}
              linkType="download"
              onClick={() => window.open(certificate.url, '_blank')}
            />,
          )
        })
      }

      if (item.badges && item.badges.length > 0) {
        item.badges?.forEach((badge) => {
          actions.push(
            <ButtonNav
              key={badge.image_url}
              variant="secondary"
              label={strings.ACTIVITY_HISTORY.TABLE.VIEW_BADGE}
              href={badge.image_url}
              linkType="external"
            />,
          )
        })
      }
      return actions
    })
  }

  /**
   *
   */
  const renderContent = () => {
    if (contentData.length > 0) {
      return (
        <PaginationScrollWrapper {...paginationProps}>
          <Table
            defaultSort={SortUtils.transformSortValues(ordering)}
            header={[
              {
                label: strings.ACTIVITY_HISTORY.TABLE.TITLE,
                sort: sortHandler,
                mobileLayout: {
                  width: 'full',
                  ascLabel: strings.ACTIVITY_HISTORY.TABLE.ASC_LABEL_A_Z,
                  descLabel: strings.ACTIVITY_HISTORY.TABLE.DESC_LABEL_A_Z,
                  showLabel: false,
                },
              },
              {
                label: strings.ACTIVITY_HISTORY.TABLE.STATUS,
                sort: sortHandler,
                mobileLayout: {
                  width: 'full',
                  ascLabel: strings.ACTIVITY_HISTORY.TABLE.ASC_LABEL_A_Z,
                  descLabel: strings.ACTIVITY_HISTORY.TABLE.DESC_LABEL_A_Z,
                  showLabel: false,
                },
              },
              {
                label: strings.ACTIVITY_HISTORY.TABLE.DETAILS,
                mobileLayout: {
                  width: 'half',
                  showLabel: true,
                },
              },
              {
                label: strings.ACTIVITY_HISTORY.TABLE.EARNED,
                mobileLayout: {
                  width: 'half',
                  showLabel: true,
                },
              },
            ]}
            rows={tableRows()}
            actions={{
              label: strings.ACTIVITY_HISTORY.TABLE.ACTIONS,
              items: rowActions(),
            }}
          />
          <PaginationWrapper>
            <Pagination {...paginationProps} count={meta?.totalPages} />
          </PaginationWrapper>
        </PaginationScrollWrapper>
      )
    }
    return (
      <div>
        <Typography className={classes.textTitle} component={'h3'}>
          {strings.ACTIVITY_HISTORY.COURSES.NO_RESULTS_MESSAGE}
        </Typography>
        <Typography className={classes.textDescription} component={'p'}>
          {strings.ACTIVITY_HISTORY.COURSES.NO_RESULTS_DESCRIPTION}
        </Typography>
        <div className={classes.textAction}>
          <Button href={RouteNames.SEARCH_COURSES}>
            {strings.ACTIVITY_HISTORY.COURSES.SEE_COURSES}
          </Button>
        </div>
      </div>
    )
  }

  /**
   *
   * @param count
   */
  const formatCount = (count: any | null): string => {
    if (count) return `(${count})`
    return ''
  }

  /**
   *
   */
  return (
    <Box
      header={{
        title: `${strings.ACTIVITY_HISTORY.COURSES.TITLE} ${formatCount(
          data?.learningCourse?.meta?.count,
        )}`,
        actionsAlignment: 'right',
        actions: contentData.length ? (
          <Button
            variant="tertiary"
            onClick={() => {
              Helper.openInNewTab(ExternalRouteNames.TRANSCRIPT_DOWNLOAD)
            }}
          >
            <Icon glyph="download" size={16} color={colors.ctaPrimary} />
            <Spacer size={8} />
            <span>Download Transcript</span>
          </Button>
        ) : null,
      }}
    >
      <ConditionalLoader condition={!isLoading}>
        {renderContent()}
      </ConditionalLoader>
    </Box>
  )
}

export default Courses
